// fetch-wrapper.ts
import { setUserToken } from "../../features/userSlice";
import { authService } from "../../logic/authService";
import store from "../../store";
import { navigateTo } from "../../utils/navigation";
import { loginService } from "../login.service";

// ==================== Types ====================
interface IResponse {
  ok: boolean;
  text: () => Promise<string>;
  statusText: string;
  status: number;
}

// ==================== Token Expiration Management ====================
function isTokenExpired(): boolean {
  const tokenExpiration = authService.getAccessTokenExpiration();
  if (!tokenExpiration) return true;

  const expirationTime = new Date(tokenExpiration).getTime();
  const currentTime = Date.now();

  return expirationTime <= currentTime;
}

// ==================== Token Refresh Management ====================
let isRefreshInProgress = false;
let refreshingPromise: Promise<void> | null = null;

function updateTokens(data: any): void {
  authService.setToken(data.access_token);
  store.dispatch(setUserToken(data.access_token));
  authService.setAccessTokenExpiration(data.access_token_expires_at);
}

function handleRefreshFailure(): void {
  authService.clearToken();
  navigateTo("/login");
  throw new Error("Refresh failed");
}

async function performRefresh(): Promise<void> {
  try {
    const result = await loginService.refresh();

    if (result?.data?.access_token) {
      updateTokens(result.data);
    } else {
      handleRefreshFailure();
    }
  } catch (error) {
    handleRefreshFailure();
    throw error;
  }
}

async function refreshAccessToken(): Promise<void> {
  if (isRefreshInProgress) {
    return refreshingPromise!;
  }

  isRefreshInProgress = true;
  refreshingPromise = performRefresh();

  try {
    await refreshingPromise;
  } finally {
    isRefreshInProgress = false;
    refreshingPromise = null;
  }
}

async function ensureValidToken(): Promise<void> {
  const token = authService.getToken();

  if (!token) {
    navigateTo("/login");
    throw new Error("No token available");
  }

  // Check if token is expired
  if (isTokenExpired()) {
    await refreshAccessToken();
  }
}

// ==================== Request Builder ====================
function buildHeaders(
  token?: string,
  contentType?: string
): Record<string, string> {
  const headers: Record<string, string> = {};

  if (contentType) {
    headers["Content-Type"] = contentType;
  }

  if (token) {
    headers.Authorization = token;
  }

  return headers;
}

// FIX: Always fetch fresh token, use oauthTokenHeader only as fallback
function getAuthToken(oauthTokenHeader?: string): string | undefined {
  // Always prioritize fresh token from authService
  const freshToken = authService.getToken();

  if (freshToken) {
    return `Bearer ${freshToken}`;
  }

  // Fallback to provided header only if no token in storage
  // (this case should rarely happen in practice)
  if (oauthTokenHeader) {
    return `Bearer ${oauthTokenHeader}`;
  }

  return undefined;
}

function buildRequestOptions(
  method: string,
  token?: string,
  body?: any,
  contentType: string = "application/json"
): RequestInit {
  const options: RequestInit = {
    method,
    headers: buildHeaders(token, contentType),
    credentials: "include",
  };

  if (body) {
    if (body instanceof FormData) {
      options.body = body;
      // Remove Content-Type to let browser set it with boundary
      delete (options.headers as Record<string, string>)["Content-Type"];
    } else if (contentType === "application/x-www-form-urlencoded") {
      options.body = body.toString();
    } else {
      options.body = JSON.stringify(body);
    }
  }

  return options;
}

// ==================== Response Handler ====================

async function handleErrorResponse(
  response: IResponse,
  text: string,
  requestBuilder: () => Promise<Response>,
  isRetry: boolean
): Promise<any> {
  // Only retry once on 401
  if (response.status === 401 && !isRetry) {
    try {
      await refreshAccessToken();
      // Now retry with fresh token (requestBuilder will fetch it fresh)
      const retryResponse = await requestBuilder();
      return handleResponse(retryResponse as any, requestBuilder, true);
    } catch (e) {
      return Promise.reject({ status: 401, text });
    }
  }

  // Try to parse the error text as JSON
  let parsedError;
  try {
    parsedError = JSON.parse(text);
  } catch {
    parsedError = text; // Keep as string if not valid JSON
  }

  let message: string;

  if (parsedError && typeof parsedError === "object") {
    message =
      parsedError.reason ||
      parsedError.detail ||
      parsedError.message ||
      parsedError.error ||
      JSON.stringify(parsedError);
  } else {
    message = parsedError || text || "Unexpected error";
  }

  const errorObject = {
    response,
    text,
    parsedText: message,
    status: response.status,
  };
  return Promise.reject(errorObject);
}

function parseSuccessResponse(response: IResponse, text: string): any {
  try {
    const data = JSON.parse(text);
    return { status: response.status, data };
  } catch {
    return { status: response.status, data: text };
  }
}

async function handleResponse(
  response: IResponse,
  requestBuilder: () => Promise<Response>,
  isRetry: boolean = false
): Promise<any> {
  if (response.status === 204) {
    return { status: 204, data: null };
  }

  const text = await response.text().catch(() => "");

  if (!response.ok) {
    return handleErrorResponse(response, text, requestBuilder, isRetry);
  }

  return parseSuccessResponse(response, text);
}

function extractFilename(response: Response): string | undefined {
  const contentDisposition = response.headers.get("Content-Disposition");
  if (!contentDisposition) return undefined;

  const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
  return filenameMatch ? filenameMatch[1] : undefined;
}

async function handleCSVResponse(response: Response): Promise<any> {
  if (!response.ok) {
    const errorData = await response.text().catch(() => "");
    throw new Error(
      `HTTP error! status: ${response.status}, body: ${errorData}`
    );
  }

  const filename = extractFilename(response);
  const blob = await response.blob();

  if (blob.size === 0) {
    throw new Error("Received empty file");
  }

  return { status: response.status, data: blob, filename };
}

// ==================== HTTP Methods ====================
async function prepareRequest(skipAuth: boolean = false): Promise<void> {
  if (!skipAuth) {
    await ensureValidToken();
  }
}

async function get(
  url: string,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  // FIX: Don't capture oauthTokenHeader in closure, call getAuthToken() each time
  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("GET", token);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function post(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("POST", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function put(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("PUT", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function patch(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("PATCH", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function deleteRequest(
  url: string,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("DELETE", token);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function postForCSV(
  url: string,
  body: any,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions("POST", token, body);
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

async function getForCSV(
  url: string,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh
  const options = buildRequestOptions("GET", token);

  return fetch(url, options).then(handleCSVResponse);
}

async function postURLEncoded(
  url: string,
  body: object,
  oauthTokenHeader?: string,
  skipAuth: boolean = false
): Promise<any> {
  await prepareRequest(skipAuth);

  const makeRequest = () => {
    const token = skipAuth ? undefined : getAuthToken(oauthTokenHeader); // Fetches fresh on each call
    const options = buildRequestOptions(
      "POST",
      token,
      body,
      "application/x-www-form-urlencoded"
    );
    return fetch(url, options);
  };

  return makeRequest().then((res) => handleResponse(res as any, makeRequest));
}

function getUrl(url: string): Promise<Response> {
  return fetch(url, { credentials: "include" });
}

// ==================== Public API ====================
export const fetchWrapper = {
  get,
  post,
  postForCSV,
  put,
  patch,
  delete: deleteRequest,
  postURLEncoded,
  getForCSV,
  getUrl,
};
